import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, useRef } from 'react';

/**
 * Virtualizace dlouhých seznamů (fáze 15): do `threshold` řádků se vykreslí vše (jednodušší
 * DOM, testy), nad ním jen viditelné řádky + rezerva; výška řádku se měří skutečná.
 */
export function VirtualRows<T>({
  items,
  render,
  estimate = 64,
  threshold = 100,
}: {
  items: T[];
  render: (item: T, index: number) => ReactNode;
  estimate?: number;
  threshold?: number;
}) {
  if (items.length <= threshold) return <>{items.map(render)}</>;
  return <Windowed items={items} render={render} estimate={estimate} />;
}

function Windowed<T>({
  items,
  render,
  estimate,
}: {
  items: T[];
  render: (item: T, index: number) => ReactNode;
  estimate: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const v = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => estimate,
    overscan: 10,
    scrollMargin: ref.current?.offsetTop ?? 0,
    // bez layoutu (jsdom) drž odhad, ať se nevykreslí celý seznam
    measureElement: (el) => el.getBoundingClientRect().height || estimate,
  });
  return (
    <div ref={ref} style={{ height: v.getTotalSize(), position: 'relative' }} data-virtual="1">
      {v.getVirtualItems().map((row) => (
        <div
          key={row.key}
          data-index={row.index}
          ref={v.measureElement}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${row.start - v.options.scrollMargin}px)`,
          }}
        >
          {render(items[row.index] as T, row.index)}
        </div>
      ))}
    </div>
  );
}
